import { bootstrap as sharedBootstrap } from 'owa-shared-bootstrap';
import { BootstrapOptions } from './types/BootstrapOptions';
// Import setHtmlLangOrchestrator so that it gets run.
import './orchestrators/setHtmlLangOrchestrator';
// Need to wait for flags before initializing themes
// since themes depend on flags.
import { waitForFlagsInitialization } from './orchestrators/waitForFlagsInitialization';
import 'owa-fabric-theme'; // Need to initialize orchestrators that listen for theme and locale change
import { DesktopErrorComponentAsync } from 'owa-desktop-error-component';
import { isOpx, getOpxHostApi } from 'owa-opx';
import { getWindowTitle } from './initialization/getWindowTitle';
import { initializeOwaLocalization } from './initialization/initializeOwaLocalization';
import { initializeManifest } from './initialization/initializeManifest';
import initializeTheme from './initialization/initializeTheme';
import initializeDensity from './initialization/initializeDensity';
import { getBposPromise } from 'owa-bpos-store';
import { lazyPwaUtils } from 'owa-pwa-utils';
import { updateServiceConfig } from 'owa-service/lib/config';
import { getMailboxSpecificRequestOptions } from 'owa-request-options';
import { isPwa, isNative } from 'owa-config';
import { initializeApolloClient, lazyUnblockBootResolvers } from 'owa-apollo';
import { lazyNativeResolvers } from 'outlook-resolvers-native';
import { lazyWebResolvers } from 'outlook-resolvers-web';
import { isBootFeatureEnabled } from 'owa-metatags';
import { lazyCreateManagedQueryLink } from 'owa-managed-query-link';
import { PromiseWithKey } from 'owa-performance';

export async function bootstrap(options: BootstrapOptions) {
    const prerenderPromises: PromiseWithKey<unknown>[] = [
        {
            promise: waitForFlagsInitialization.then(async () => {
                if (isPwa()) {
                    await lazyPwaUtils.import();
                }
                initializeManifest();
                return initializeTheme();
            }),
            key: 'th',
        },
    ];

    prerenderPromises.push({
        promise: waitForFlagsInitialization.then(async () => initializeDensity()),
        key: 'dnst',
    });

    const bposPromise = getBposPromise();
    if (bposPromise) {
        prerenderPromises.push({ promise: bposPromise, key: 'bpos' });
    }

    // Initialize the Apollo client
    const lazyResolvers = isNative() ? lazyNativeResolvers : lazyWebResolvers;
    const createManagedQueryLink = isNative() ? lazyCreateManagedQueryLink.import() : undefined;
    let initializeApolloPromise = initializeApolloClient({
        lazyResolvers: lazyResolvers.import(),
        lazyCreateManagedQueryLink: createManagedQueryLink,
        sessionPromise: options.sessionPromise,
    });

    if (isBootFeatureEnabled('apollo')) {
        if (options.prepareApolloCacheForRender) {
            initializeApolloPromise = initializeApolloPromise.then(apolloClient => {
                options.prepareApolloCacheForRender();
                return apolloClient;
            });
        }

        prerenderPromises.push({ key: 'apollo', promise: initializeApolloPromise });
    }

    updateServiceConfig({
        // This is used in owa-service to get Request Options with appropriate headers
        // needed for multi-account support. Parameters: UserIdentity, MailboxSmtpAddress and MailboxType
        // passed within getMailboxSpecificRequestOptions are used to decide which headers to be added for the same.
        prepareRequestOptions: getMailboxSpecificRequestOptions,
    });

    return sharedBootstrap({
        ...options,
        getWindowTitle: () =>
            isOpx() ? options.getModuleName() : getWindowTitle(options.getModuleName),
        initializeLocalization: initializeOwaLocalization,
        fullErrorComponent: DesktopErrorComponentAsync,
        prerenderPromises,
        onComponentError: () => {
            if (isOpx()) {
                getOpxHostApi().onFatalError();
            }
        },
    }).then(() => {
        // apollo will have already been imported if its enabled
        if (isBootFeatureEnabled('apollo')) {
            lazyUnblockBootResolvers.importAndExecute();
        }
    });
}
